import BookIcon from "@ecologi/assets/icons/book.svg";
import LocationIcon from "@ecologi/assets/icons/location_alt.svg";
import {
  getProjectMapUrl,
  Impact,
  projectTypeMapper,
  User,
  Wordpress,
  isTree,
} from "@ecologi/shared";
import styled from "@emotion/styled";
import React from "react";
import { ForestModalBase } from "./ForestModalBase";
import { ModalInfoCard } from "./ModalInfoCard";
import { projectSubTypeVisuals, projectTypeVisuals } from "../consts";
import {
  getAgeInDays,
  getFundedBy,
  getFundedDaysAgo,
  getLocationAndProjectLabel,
  getModalImagePath,
  getTitle,
} from "./utils";
import {
  ProjectContent,
  Title,
  TitleWrapper,
  Text,
  Grid,
  GridIcon,
  StyledLink,
  Bold,
} from "./styles/ForestModal.styles";

const ProjectIcon = styled.div`
  display: flex;
  flex-direction: column;
  width: 32px;
  margin-right: 8px;
  flex-shrink: 0;
`;

const ProjectIconWrapper = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 12px;

  @media ${({ theme }) => theme.bp.tabletUp} {
    max-width: 70%;
  }
`;

export const ForestImpactModal = ({
  closePopup,
  isOpen,
  isOwnProfile,
  project,
  tileInfo: { impact, isMocked },
  user,
  imageLabel,
}: {
  closePopup: () => void;
  isOpen: boolean;
  isOwnProfile: boolean;
  project: Wordpress.WordpressProjectType;
  tileInfo: { impact: Impact; isMocked: boolean };
  user: User | null;
  imageLabel?: string;
}) => {
  if (isTree(impact)) return null;

  const title = getTitle(impact);
  const { projectType, projectSubType } = impact.project || {};
  const projectIcon =
    (projectSubType && projectSubTypeVisuals[projectSubType]) ||
    (projectType && projectTypeVisuals[projectType]);

  const fundedBy = getFundedBy(user, impact);
  const fundedDaysAgo = getFundedDaysAgo(getAgeInDays(impact));

  const [location, projectLabel] = getLocationAndProjectLabel(impact, project);
  const projectHref = `/projects/${project.slug}`;
  const projectMapUrl = getProjectMapUrl(project.location);

  return (
    <ForestModalBase
      closePopup={closePopup}
      enableSharing={!isMocked}
      imagePath={getModalImagePath(impact, project)}
      imageLabel={imageLabel}
      isOpen={isOpen}
    >
      <>
        <ProjectContent>
          <ProjectIconWrapper>
            {projectIcon && <ProjectIcon>{projectIcon.icon}</ProjectIcon>}
            {projectType && projectTypeMapper[projectType].title}
          </ProjectIconWrapper>

          <TitleWrapper>
            <Title data-testid="forest-modal-project-title">{title}</Title>
          </TitleWrapper>

          <>
            {fundedDaysAgo && (
              <Text data-testid="forest-modal-funded-by">
                <Bold>{fundedBy ? "Funded by: " : "Funded: "}</Bold>
                {fundedBy ? `${fundedBy}, ${fundedDaysAgo}` : fundedDaysAgo}
              </Text>
            )}

            <Grid>
              {projectMapUrl && (
                <>
                  <GridIcon>
                    <LocationIcon />
                  </GridIcon>

                  <Text>
                    <StyledLink
                      href={projectMapUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      title={`View the project ${
                        location ? `in ${location} ` : ""
                      }(new tab)`}
                    >
                      {location || "Map"}
                    </StyledLink>
                  </Text>
                </>
              )}

              {projectLabel && projectHref && (
                <>
                  <GridIcon>
                    <BookIcon />
                  </GridIcon>

                  <Text>
                    <StyledLink
                      href={projectHref}
                      rel="noopener noreferrer"
                      target="_blank"
                      title="View project"
                    >
                      {projectLabel}
                    </StyledLink>
                  </Text>
                </>
              )}
            </Grid>
          </>
        </ProjectContent>

        <ModalInfoCard impact={impact} isOwnProfile={isOwnProfile} />
      </>
    </ForestModalBase>
  );
};
